import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Eye, BookOpen, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Badge, statusTone, Money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { CourseForm, EMPTY_COURSE } from "@/components/course/CourseForm";
import { ConfirmDelete } from "@/components/course/ConfirmDelete";
import CourseEditor from "./CourseEditor";
import { toast } from "sonner";

export default function CourseBuilder() {
  const { user, can } = useAuth();
  const [courses, setCourses] = useState(null);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const canDelete = user?.role === "educator" || can("courses", "delete");

  const load = () => api.get("/my/courses").then(({ data }) => setCourses(data));
  useEffect(() => { load(); api.get("/categories").then(({ data }) => setCats(data)); }, []);

  const remove = async (c) => {
    try { await api.delete(`/courses/${c.id}`); toast.success("Course deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (editing) return <CourseEditor courseId={editing.id} tab={editing.tab} cats={cats} onBack={() => { setEditing(null); load(); }} />;
  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";

  return (
    <div data-testid="course-builder">
      <PageHeader title="Courses" subtitle="Create, edit and manage all your courses" action={<NewCourseDialog cats={cats} onCreated={(c) => { load(); setEditing({ id: c.id, tab: "curriculum" }); }} />} />
      {courses && courses.length === 0 ? <EmptyState icon={BookOpen} title="No courses yet" subtitle="Create your first course to get started." /> : (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <div className="hidden md:grid grid-cols-[1fr_140px_110px_90px_110px_170px] gap-4 px-4 py-3 text-xs font-semibold uppercase text-muted-foreground border-b border-border bg-muted/30">
            <span>Course</span><span>Category</span><span>Price</span><span>Students</span><span>Status</span><span className="text-right">Actions</span>
          </div>
          {(courses || []).map((c) => (
            <div key={c.id} className="grid grid-cols-1 md:grid-cols-[1fr_140px_110px_90px_110px_170px] gap-3 md:gap-4 items-center px-4 py-3 border-b border-border last:border-0" data-testid={`course-row-${c.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                {c.thumbnail ? <img src={c.thumbnail} alt="" className="h-12 w-20 object-cover rounded shrink-0" /> : <div className="h-12 w-20 rounded bg-muted flex items-center justify-center shrink-0"><BookOpen size={18} className="text-muted-foreground" /></div>}
                <div className="min-w-0">
                  <div className="font-semibold text-navy truncate">{c.title}</div>
                  <div className="text-xs text-muted-foreground capitalize">{c.course_type} • {c.level}{c.instructor_name ? ` • ${c.instructor_name}` : ""}</div>
                </div>
              </div>
              <span className="text-sm text-muted-foreground truncate"><span className="md:hidden font-medium">Category: </span>{catName(c.category_id)}</span>
              <span className="text-sm font-semibold"><span className="md:hidden font-medium text-muted-foreground">Price: </span><Money amount={c.discount_price || c.price} /></span>
              <span className="text-sm inline-flex items-center gap-1"><Users size={13} /> {c.enrolled_count}<span className="md:hidden"> students</span></span>
              <span><Badge tone={statusTone(c.status)}>{c.status}</Badge></span>
              <div className="flex md:justify-end gap-1">
                <Button size="sm" variant="outline" onClick={() => setEditing({ id: c.id, tab: "details" })} data-testid={`edit-course-${c.id}`}><Pencil size={14} className="mr-1" /> Edit</Button>
                <a href={`/courses/${c.id}`} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" title="Preview" data-testid={`preview-course-${c.id}`}><Eye size={15} /></Button></a>
                {canDelete && (
                  <ConfirmDelete testid={`delete-course-${c.id}`} title="Delete this course?" onConfirm={() => remove(c)}
                    description={`"${c.title}" with all its sections and lessons will be deleted permanently. This cannot be undone.`}
                    trigger={<Button size="sm" variant="ghost" className="text-crimson hover:text-crimson" title="Delete" data-testid={`delete-course-${c.id}`}><Trash2 size={15} /></Button>} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewCourseDialog({ cats, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY_COURSE);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const create = async () => {
    if (!f.title.trim()) return toast.error("Enter a course title");
    if (f.price === "" || Number(f.price) < 0) return toast.error("Enter a valid price (0 for free)");
    try { const { data } = await api.post("/courses", f); toast.success("Course created — now add sections & lessons"); setOpen(false); setF(EMPTY_COURSE); onCreated(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="viriks-navy text-white" data-testid="new-course-btn"><Plus size={16} className="mr-1" /> New Course</Button></DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>Create Course</DialogTitle></DialogHeader>
        <CourseForm f={f} set={set} cats={cats} />
        <DialogFooter><Button className="viriks-navy text-white" onClick={create} data-testid="nc-create">Create Course</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
